import React, { useEffect, useState } from "react";
import { IconChevronDown } from "../components/ui/icons";
import DatePicker from "../components/ui/DatePicker";
import MultiSelectDropdown from "../components/ui/MultiSelectDropdown";
import Button from "../components/ui/Button";
import Select from "../components/ui/Select";
import ExportButtonGroup from "../components/ui/ExportButtonGroup";
import { Skeleton } from "../components/ui/Skeleton";
import { EmptyState } from "../components/ui/StateBlock";
import Card from "../components/ui/Card";
import { useApiData } from "../hooks/useApiData";
import { useWarehouses } from "../hooks/useWarehouses";
import { unwrapArray } from "../utils/api";
import { exportSectionsToPDF, exportToExcel, printSections } from "../utils/exportUtils";
import { todayInputValue } from "../utils/date";
import { formatQty } from "../utils/format";

// The "Predlog" field: slightly yellow to show it is the only thing edited by hand here.
const SUGGESTION_INPUT =
  "h-8 w-24 rounded-md border border-warning/40 bg-warning/10 px-2 text-right text-sm font-semibold tabular-nums text-slate-900 " +
  "transition-colors hover:border-warning focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/25 " +
  "dark:text-slate-50 dark:focus:border-primary-light dark:focus:ring-primary-light/25";

export default function Consumption() {
  const [startDate, setStartDate] = useState(todayInputValue);
  const [endDate, setEndDate] = useState(todayInputValue);
  const [selectedWarehouses, setSelectedWarehouses] = useState([]);
  const [selectedSuppliers, setSelectedSuppliers] = useState([]);
  const [fetchPath, setFetchPath] = useState(null);
  const [items, setItems] = useState([]);

  const { warehouses } = useWarehouses();

  const { data: appSettingsRaw } = useApiData("/settings_get.php");
  useEffect(() => {
    if (appSettingsRaw?.success && appSettingsRaw.default_magacin_potrosnja) {
      setSelectedWarehouses(appSettingsRaw.default_magacin_potrosnja.split(",").filter(Boolean));
    }
  }, [appSettingsRaw]);

  const { data: suppliersRaw } = useApiData("/suppliers.php");
  // Same story as with warehouses - we do not know in advance whether the fields arrive
  // as id/name or under another name, so both cases are covered.
  const suppliers = unwrapArray(suppliersRaw).map((s) => ({
    id: String(s.id ?? s.supplierid ?? s.dobavljac_id ?? ""),
    name: s.name ?? s.naziv ?? s.dobavljac ?? "",
  }));

  const { data, loading } = useApiData(fetchPath);

  // Local copy of the data, because the "Predlog" column is edited by hand (user input)
  // - it must not be overwritten directly from the fetch response without this synchronization.
  useEffect(() => {
    // The purchase suggestion always rounds up to the next whole quantity (e.g. 2,3 -> 3).
    if (data !== null) setItems(unwrapArray(data).map((p) => ({ ...p, predlog: Math.ceil(parseFloat(p.predlog) || 0) })));
  }, [data]);

  // Source of consumption: documents (T/F/L/W) or expenses (storno) converted to raw materials through the recipe.
  // They are never added together: one or the other is shown.
  const [source, setSource] = useState("documents");
  const buildPath = (src) => {
    const params = new URLSearchParams({ od: startDate, do: endDate });
    if (selectedWarehouses.length) params.append("warehouses", selectedWarehouses.join(","));
    if (selectedSuppliers.length) params.append("suppliers", selectedSuppliers.join(","));
    params.append("source", src);
    return `/consumption.php?${params.toString()}`;
  };
  const handleShow = () => {
    if (!startDate || !endDate) return;
    setFetchPath(buildPath(source));
  };
  const changeSource = (value) => {
    setSource(value);
    // already showing a table: reload it right away with the same filters
    if (fetchPath) setFetchPath(buildPath(value));
  };

  const updatePredlog = (id, newValue) => {
    setItems((prev) => prev.map((p) => (p.id === id ? { ...p, predlog: Math.max(0, Math.ceil(parseFloat(newValue) || 0)) } : p)));
  };

  // Expenses source: no stock comparison, the suggestion is simply what was consumed.
  const expensesMode = source === "expenses";
  const [hideEmpty, setHideEmpty] = useState(true);
  const [onlyWithSuggestion, setOnlyWithSuggestion] = useState(false);
  const displayedData = items.filter((p) => {
    if (hideEmpty && (expensesMode || parseFloat(p.lager) === 0) && parseFloat(p.potroseno) === 0) return false;
    if (onlyWithSuggestion && parseFloat(p.predlog) <= 0) return false;
    return true;
  });

  // Shopping list: only what is to be bought (suggestion above 0), just the article and its quantity - no stock.
  const shoppingRows = displayedData.filter((p) => parseFloat(p.predlog) > 0);
  const shoppingSection = () => ({
    head: ["Br.", "Artikal", "Količina"],
    body: shoppingRows.map((p, i) => [`${i + 1}.`, p.naziv, parseFloat(p.predlog) || 0]),
    decimals: [null, null, 0],
    ruled: true,
    columnStyles: { 0: { cellWidth: 36 }, 2: { cellWidth: 90 } },
  });

  // Excel keeps the full picture (stock, consumption, suggestion, supplier) with a bold "Ukupno" row.
  const handleExportExcel = () =>
    exportToExcel(
      displayedData.map((p) => ({
        Naziv: p.naziv,
        ...(expensesMode ? {} : { Lager: parseFloat(p.lager) || 0 }),
        Potrošeno: parseFloat(p.potroseno) || 0,
        Predlog: parseFloat(p.predlog) || 0,
        Dobavljač: p.dobavljac || "",
      })),
      "nabavka.xlsx",
      "Nabavka",
      { title: "Nabavka" }
    );

  const handleExportPdf = () =>
    exportSectionsToPDF({ title: "Lista za nabavku", sections: [shoppingSection()], filename: "lista_za_nabavku.pdf" });

  const handlePrint = () => printSections({ title: "Lista za nabavku", sections: [shoppingSection()] });

  const exportDisabled = displayedData.length === 0;
  const listDisabled = shoppingRows.length === 0;
  const [expandedId, setExpandedId] = useState(null);

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-end gap-x-3 gap-y-3 rounded-xl border border-slate-200 bg-surface p-3 dark:border-slate-800 dark:bg-slate-900">
        <DatePicker label="Od" value={startDate} onChange={setStartDate} />
        <DatePicker label="Do" value={endDate} onChange={setEndDate} />
        <MultiSelectDropdown
          label="Magacini"
          options={warehouses.map((w) => ({ value: w.id, label: w.name }))}
          selected={selectedWarehouses}
          onChange={setSelectedWarehouses}
        />
        <MultiSelectDropdown
          label="Dobavljači"
          options={suppliers.map((s) => ({ value: s.id, label: s.name }))}
          selected={selectedSuppliers}
          onChange={setSelectedSuppliers}
        />
        <Select
          label="Izvor potrošnje"
          value={source}
          onChange={(e) => changeSource(e.target.value)}
          options={[
            { value: "documents", label: "Dokumenta" },
            { value: "expenses", label: "Rashod (normativ)" },
          ]}
        />
        <Button onClick={handleShow} disabled={!startDate || !endDate}>
          Prikaži
        </Button>

        <label className="flex h-9 cursor-pointer items-center gap-2 text-sm text-slate-600 dark:text-slate-300">
          <input
            type="checkbox"
            checked={hideEmpty}
            onChange={(e) => setHideEmpty(e.target.checked)}
            className="h-4 w-4 rounded accent-primary"
          />
          Sakrij artikle bez prometa
        </label>
        <label className="flex h-9 cursor-pointer items-center gap-2 text-sm text-slate-600 dark:text-slate-300">
          <input
            type="checkbox"
            checked={onlyWithSuggestion}
            onChange={(e) => setOnlyWithSuggestion(e.target.checked)}
            className="h-4 w-4 rounded accent-primary"
          />
          Samo sa predlogom nabavke
        </label>

        <div className="w-full border-t border-slate-100 pt-3 dark:border-slate-800 sm:ml-auto sm:w-auto sm:border-0 sm:pt-0">
          <ExportButtonGroup
            onExcel={handleExportExcel}
            onPdf={listDisabled ? undefined : handleExportPdf}
            onPrint={listDisabled ? undefined : handlePrint}
            disabled={exportDisabled}
            pdfPrintDisabled={listDisabled}
            size="sm"
          />
        </div>
      </div>

      {/* Desktop/tablet: full table */}
      <Card padded={false} className="hidden md:block">
        <table className="w-full table-fixed border-collapse text-sm">
          <thead>
            <tr>
              {[
                { h: "Naziv", w: expensesMode ? "36%" : "30%", right: false },
                ...(expensesMode ? [] : [{ h: "Lager", w: "13%", right: true }]),
                { h: "Potrošeno", w: expensesMode ? "18%" : "14%", right: true },
                { h: "Predlog", w: expensesMode ? "20%" : "17%", right: true },
                { h: "Dobavljač", w: expensesMode ? "26%" : "26%", right: false },
              ].map(({ h, w, right }) => (
                <th
                  key={h}
                  scope="col"
                  style={{ width: w }}
                  className={`sticky top-0 border-b border-slate-200 bg-slate-50 px-3 py-2 text-xs font-medium text-slate-500
                    first:rounded-tl-[9px] last:rounded-tr-[9px] dark:border-slate-800 dark:bg-slate-900 dark:text-slate-400 ${
                      right ? "text-right" : "text-left"
                    }`}
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
            {loading &&
              displayedData.length === 0 &&
              Array.from({ length: 6 }, (_, i) => (
                <tr key={i}>
                  {(expensesMode ? [0, 1, 2, 3] : [0, 1, 2, 3, 4]).map((c) => (
                    <td key={c} className="px-3 py-2.5">
                      <Skeleton className={`h-3.5 ${c === 0 || c === (expensesMode ? 3 : 4) ? "w-3/4" : "ml-auto w-10"}`} />
                    </td>
                  ))}
                </tr>
              ))}
            {!loading && displayedData.length === 0 && (
              <tr>
                <td colSpan={expensesMode ? 4 : 5}>
                  <EmptyState
                    title={items.length === 0 ? "Izaberi period i klikni Prikaži" : "Nema artikala za prikazane filtere"}
                    hint={items.length === 0 ? undefined : "Isključi neki od filtera iznad da vidiš više artikala."}
                  />
                </td>
              </tr>
            )}
            {displayedData.map((p) => (
              <tr key={p.id} className={`transition-colors hover:bg-slate-50 dark:hover:bg-slate-800/50 ${loading ? "opacity-60" : ""}`}>
                <td className="break-words px-3 py-2 text-slate-800 dark:text-slate-100">{p.naziv}</td>
                {!expensesMode && <td className="px-3 py-2 text-right tabular-nums text-slate-700 dark:text-slate-200">{formatQty(p.lager)}</td>}
                <td className="px-3 py-2 text-right tabular-nums text-slate-700 dark:text-slate-200">{formatQty(p.potroseno)}</td>
                <td className="px-3 py-1.5 text-right">
                  <input
                    type="number"
                    min="0"
                    step="1"
                    aria-label={`Predlog nabavke: ${p.naziv}`}
                    value={p.predlog}
                    onChange={(e) => updatePredlog(p.id, e.target.value)}
                    onFocus={(e) => e.target.select()}
                    className={SUGGESTION_INPUT}
                  />
                </td>
                <td className="break-words px-3 py-2 text-slate-600 dark:text-slate-300">{p.dobavljac}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      {/* Mobile: compact rows, name + suggestion visible at once, tapping the name opens the rest */}
      <div className="flex flex-col gap-2 md:hidden">
        {loading && displayedData.length === 0 &&
          Array.from({ length: 5 }, (_, i) => <Skeleton key={i} className="h-14 w-full rounded-lg" />)}
        {!loading && displayedData.length === 0 && (
          <Card>
            <EmptyState
              title={items.length === 0 ? "Izaberi period i klikni Prikaži" : "Nema artikala za prikazane filtere"}
              hint={items.length === 0 ? undefined : "Isključi neki od filtera iznad da vidiš više artikala."}
            />
          </Card>
        )}
        {displayedData.map((p) => {
          const expanded = expandedId === p.id;
          return (
            <div
              key={p.id}
              className={`rounded-lg border border-slate-200 bg-surface px-3 py-2.5 dark:border-slate-800 dark:bg-slate-900 ${loading ? "opacity-60" : ""}`}
            >
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  aria-expanded={expanded}
                  onClick={() => setExpandedId(expanded ? null : p.id)}
                  className="flex min-w-0 flex-1 items-center gap-1.5 text-left text-sm font-medium text-slate-900 dark:text-slate-50"
                >
                  <IconChevronDown
                    aria-hidden="true"
                    className={`shrink-0 text-sm text-slate-400 transition-transform duration-150 ${expanded ? "rotate-180" : ""}`}
                  />
                  <span className="truncate">{p.naziv}</span>
                </button>
                <input
                  type="number"
                  min="0"
                  step="1"
                  aria-label={`Predlog nabavke: ${p.naziv}`}
                  value={p.predlog}
                  onChange={(e) => updatePredlog(p.id, e.target.value)}
                  onFocus={(e) => e.target.select()}
                  className={`${SUGGESTION_INPUT} shrink-0`}
                />
              </div>
              {expanded && (
                <dl className={`mt-2.5 grid gap-3 border-t border-slate-100 pt-2.5 text-xs dark:border-slate-800 ${expensesMode ? "grid-cols-2" : "grid-cols-3"}`}>
                  {!expensesMode && (
                    <div>
                      <dt className="text-slate-500 dark:text-slate-400">Lager</dt>
                      <dd className="font-medium tabular-nums text-slate-700 dark:text-slate-200">{formatQty(p.lager)}</dd>
                    </div>
                  )}
                  <div>
                    <dt className="text-slate-500 dark:text-slate-400">Potrošeno</dt>
                    <dd className="font-medium tabular-nums text-slate-700 dark:text-slate-200">{formatQty(p.potroseno)}</dd>
                  </div>
                  <div className="min-w-0">
                    <dt className="text-slate-500 dark:text-slate-400">Dobavljač</dt>
                    <dd className="truncate font-medium text-slate-700 dark:text-slate-200">{p.dobavljac}</dd>
                  </div>
                </dl>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
